// pages/BlockedAtIntakePage.jsx
//
// Staff "Blocked at intake", on GET /admin/leads/blocked (backend:
// leads/blocked_api.py + leads/blocked_at_intake.py): the submissions that
// never became leads, and why. One request per view; the backend builds
// every block from one database snapshot, so nothing on this screen can
// contradict anything else on it. Replaces the old Rejected Submissions
// screen (PR #55) at the same route; the /admin/leads/rejected* endpoints
// it read still exist in the backend until this one is in production.
//
// Every filter lives in the query string (range or from/to, partners,
// reasons, page), so a view can be shared and Back walks through it.
// Partners and reasons from a URL wait until the first response says which
// exist (`filter_options`); anything else is dropped before a request
// carries it.
import React, { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { RefreshCw } from 'lucide-react'
import api from '../services/api'
import RangeSelect from '../components/staff-leads/RangeSelect'
import BlockedKpiTiles from '../components/blocked/BlockedKpiTiles'
import ReasonsCard from '../components/blocked/ReasonsCard'
import PartnersCard from '../components/blocked/PartnersCard'
import DailyChart from '../components/blocked/DailyChart'
import LatestTable from '../components/blocked/LatestTable'
import DetailPanel from '../components/blocked/DetailPanel'
import FilterMenu from '../components/blocked/FilterMenu'
import {
  BLOCKED_RESPONSIVE_CSS, ENDPOINT, FALLBACK_TIMEZONE, RANGE_PRESETS,
  allowedFilters, filtersToSearch, parseUrlFilters, requestParams,
} from '../components/blocked/model'

// `data` survives a refetch so the screen never blanks; `key` says which
// params it answers.
const EMPTY = { data: null, key: null, loading: false, error: null }

function useBlocked(params, tick) {
  const [state, setState] = useState(EMPTY)
  const key = JSON.stringify(params)

  useEffect(() => {
    let cancelled = false
    setState((s) => ({ ...s, loading: true, error: null }))
    api.get(ENDPOINT, { params: JSON.parse(key) })
      .then(({ data }) => { if (!cancelled) setState({ data, key, loading: false, error: null }) })
      .catch((error) => { if (!cancelled) setState((s) => ({ ...s, loading: false, error })) })
    return () => { cancelled = true }
  }, [key, tick])

  return { ...state, current: state.key === key }
}

export default function BlockedAtIntakePage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const url = parseUrlFilters(searchParams)
  // The URL's partners and reasons, not yet checked against filter_options.
  // While set, requests leave them out and nothing is shown.
  const [pending, setPending] = useState(() => (
    url.partners.length || url.reasons.length ? { partners: url.partners, reasons: url.reasons } : null
  ))
  const [tick, setTick] = useState(0)
  const [openEvent, setOpenEvent] = useState(null)

  const filters = pending ? { ...url, partners: [], reasons: [] } : url
  const req = useBlocked(requestParams(filters), tick)

  const options = req.data?.filter_options
  useEffect(() => {
    if (!pending || !options) return
    const allowed = allowedFilters(pending, options)
    setPending(null)
    setSearchParams(filtersToSearch({ ...url, ...allowed }), { replace: true })
  }, [pending, options, url, setSearchParams])

  // Every change is a new history entry; paging keeps the filters.
  const update = (patch, { resetPage = true } = {}) => {
    setSearchParams(filtersToSearch({ ...url, ...(resetPage ? { page: 1 } : {}), ...patch }))
  }
  // A reason clicked in the chart becomes the only reason filter; clicking
  // the one already filtered on clears it.
  const selectReason = (code) => update({ reasons: url.reasons.length === 1 && url.reasons[0] === code ? [] : [code] })

  const refresh = () => setTick((t) => t + 1)
  const closePanel = useCallback(() => setOpenEvent(null), [])

  const data = !pending && req.data ? req.data : null
  const timezone = data?.timezone || FALLBACK_TIMEZONE
  const tzLabel = timezone === FALLBACK_TIMEZONE ? 'ET' : timezone

  const partnerOptions = (options?.partners || [])
    .filter((p) => p.id !== undefined && p.id !== null)
    .map((p) => ({ value: String(p.id), label: p.name }))
  const reasonOptions = (options?.reasons || []).map((r) => ({ value: r.code, label: r.label }))

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <style>{BLOCKED_RESPONSIVE_CSS}</style>

      <header className="bk-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-muted)', marginBottom: 4 }}>
            Rejected submissions
          </div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>Blocked at intake</h1>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--text-muted)' }}>
            Submissions that never became leads, and why · All times {tzLabel}
          </p>
        </div>
        <div className="bk-header-actions" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {options && (
            <>
              <FilterMenu label="Partner" options={partnerOptions} selected={url.partners} onChange={(partners) => update({ partners })} />
              <FilterMenu label="Reason" options={reasonOptions} selected={url.reasons} onChange={(reasons) => update({ reasons })} />
            </>
          )}
          <RangeSelect value={url.range} onChange={(range) => update({ range })} presets={RANGE_PRESETS} allowCustom />
          <button type="button" className="btn btn-ghost btn-sm" onClick={refresh} disabled={req.loading} aria-label="Refresh">
            <RefreshCw size={14} className={req.loading ? 'bk-spin' : undefined} aria-hidden="true" />
          </button>
        </div>
      </header>

      {!data && req.error ? (
        <section className="card" role="alert" style={{ padding: 24, textAlign: 'center' }}>
          <p style={{ margin: '0 0 12px', color: 'var(--text-secondary)' }}>The blocked submissions could not be loaded.</p>
          <button type="button" className="btn btn-primary btn-sm" onClick={refresh} disabled={req.loading}>Retry</button>
        </section>
      ) : !data ? (
        <Skeleton />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, opacity: req.current ? 1 : 0.7, transition: 'opacity .15s' }}>
          {req.error && (
            <div role="alert" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, fontSize: 12, padding: '8px 12px', borderRadius: 8, color: 'var(--red)', background: 'var(--red-bg)' }}>
              Could not refresh - showing the last data loaded.
              <button type="button" className="btn btn-ghost btn-sm" onClick={refresh}>Retry</button>
            </div>
          )}
          <BlockedKpiTiles kpis={data.kpis} comparison={data.range?.comparison} reasonFiltered={url.reasons.length > 0} />
          <div className="bk-split">
            <ReasonsCard reasons={data.reasons || []} selected={url.reasons} onSelect={selectReason} />
            <PartnersCard rows={data.by_partner || []} />
          </div>
          <DailyChart rows={data.daily || []} />
          <LatestTable data={data} timezone={timezone} onView={setOpenEvent} onPage={(page) => update({ page }, { resetPage: false })} />
        </div>
      )}

      {openEvent && <DetailPanel eventId={openEvent} timezone={timezone} onClose={closePanel} />}
    </div>
  )
}

function Skeleton() {
  return (
    <div aria-busy="true" aria-label="Loading blocked submissions" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div className="bk-kpis">
        {[0, 1, 2, 3].map((i) => <div key={i} className="bk-skeleton" style={{ height: 110 }} />)}
      </div>
      <div className="bk-split">
        <div className="bk-skeleton" style={{ height: 240 }} />
        <div className="bk-skeleton" style={{ height: 240 }} />
      </div>
      <div className="bk-skeleton" style={{ height: 240 }} />
      <div className="bk-skeleton" style={{ height: 320 }} />
    </div>
  )
}
